import { Link } from 'react-router-dom';

import AuthInputField from 'components/Auth/Common/AuthInputField';
import {
  DeletedAccountExistsMessage,
  UserNotFoundSignupLinkMessage,
  PasswordNotSetMessage,
} from 'components/Auth/Login/partials/Messages';
import routes from 'constants/routes';
import { getBlockedTime } from 'helpers/numbers';
import i18n from 'i18n/config';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { DISABLE_2FA_FLOW } from './Disable2faFaceAuth.constants';
import styles from './disable2faFaceAuth.module.scss';
import useVerifyEmailPassword, {
  FRONTEND_VERIFICATION_ERRORS,
  BACKEND_ERROR_CODES,
  AllErrors,
} from './hooks/useVerifyEmailPassword';

const textEmailAddressInvalid = i18n.t<string>('errors:custom.emailAddressInvalid');

const textEmailEmpty = i18n.t<string>('errors:custom.email_empty');

const textPasswordRequired = i18n.t<string>('errors:custom.password_empty');

const textDefaultMessage = i18n.t<string>('errors:custom.defaultMessage');

const textHashLimitError = i18n.t<string>(
  `errors:custom.${BACKEND_ERROR_CODES.BCRYPT_HASH_LIMIT}_login`
);

const textEmail = i18n.t<string>('common:email');
const textPassword = i18n.t<string>('auth:password');
const textVerify = i18n.t<string>('common:verify');
const textForgotPasswordRedirect = i18n.t<string>('errors:forgotPasswordRedirect');

const textPendingVerification = i18n.t<string>('errors:user.pending_verification');

const EmailErrorMessage = ({ error }: { error: AllErrors }) => {
  switch (error.code) {
    case FRONTEND_VERIFICATION_ERRORS.INVALID_EMAIL:
      return null;
    case BACKEND_ERROR_CODES.PENDING_VERIFICATION:
      return <Box className={styles.errorMsg}>{textPendingVerification}</Box>;
    case FRONTEND_VERIFICATION_ERRORS.INVALID_EMAIL_SUBMITTED:
      return <Box className={styles.errorMsg}>{textEmailAddressInvalid}</Box>;

    case FRONTEND_VERIFICATION_ERRORS.EMAIL_REQUIRED:
      return <Box className={styles.errorMsg}>{textEmailEmpty}</Box>;
    case BACKEND_ERROR_CODES.DELETED_ACCOUNT_EXISTS:
      return (
        <DeletedAccountExistsMessage
          email={error.context.email}
          userId={error.context.user_id}
        />
      );
    case BACKEND_ERROR_CODES.USER_NOT_FOUND:
      return <UserNotFoundSignupLinkMessage />;
    default:
      return <Box className={styles.errorMsg}>{textDefaultMessage}</Box>;
  }
};

const PasswordErrorMessage = ({ error }: { error: AllErrors }) => {
  switch (error.code) {
    case FRONTEND_VERIFICATION_ERRORS.PASSWORD_REQUIRED:
      return <Box className={styles.errorMsg}>{textPasswordRequired}</Box>;
    case BACKEND_ERROR_CODES.LOGIN_BLOCKED:
      return (
        <Box className={styles.errorMsg}>
          {i18n.t<string>('errors:custom.login_blocked')}
        </Box>
      );
    case BACKEND_ERROR_CODES.INVALID_PASSWORD:
      return (
        <Box className={styles.errorMsg}>
          {error.context
            ? i18n.t<string>(`errors:custom.invalid_psw`, {
                count: error.context.max_attempts - error.context.count,
              })
            : i18n.t<string>('auth:twoFaFaceAuth.invalidPassword')}
        </Box>
      );
    case BACKEND_ERROR_CODES.VERIFY_EMAIL_BLOCKED: {
      const { unit, value } = getBlockedTime(error.context.time);

      return (
        <Box className={styles.errorMsg}>
          {i18n.t<string>(`errors:user.emailBlockedText_${unit}`, {
            timeValue: value,
            count: value,
          })}
        </Box>
      );
    }
    case BACKEND_ERROR_CODES.BCRYPT_HASH_LIMIT:
      return (
        <Box className={styles.errorMsg}>
          {textHashLimitError}
          <Link
            data-testid="forgot-password-hash-limit-error"
            to={routes.forgotPassword}
            className={styles.link}
          >
            {' '}
            {textForgotPasswordRedirect}
          </Link>
        </Box>
      );
    case BACKEND_ERROR_CODES.MFA_ALREADY_DISABLED:
      return (
        <Box className={styles.errorMsg}>
          {i18n.t<string>('auth:twoFaFaceAuth.mfaAlreadyDisabled')}
        </Box>
      );
    case BACKEND_ERROR_CODES.PASSWORD_NOT_SET:
      return <PasswordNotSetMessage />;
    case FRONTEND_VERIFICATION_ERRORS.SOMETHING_WENT_WRONG:
      return <Box className={styles.errorMsg}>{textDefaultMessage}</Box>;
    default:
      return <Box className={styles.errorMsg}>{textDefaultMessage}</Box>;
  }
};

const VerifyEmailPass = ({
  onSuccess,
  updateActiveFlow,
}: {
  onSuccess: () => void;
  updateActiveFlow: (flow: DISABLE_2FA_FLOW) => void;
}) => {
  const { state, onEmailChange, onPasswordChange, onSubmit, buttonLoading } =
    useVerifyEmailPassword({
      onvVerificationSuccess: () => onSuccess(),
      updateActiveFlow,
    });

  return (
    (<form
      className={styles.form}
      data-testid="verify-email-password-form"
      onSubmit={onSubmit}
      data-palette="VerifyEmailPass">
      <AuthInputField
        id="lost-2fa-email"
        testID="lost-2fa-email"
        data-testid="lost-2fa-email"
        value={state.email.value}
        label={textEmail}
        type="email"
        onChange={e => onEmailChange(e.target.value)}
        hideValidationCorrectIcon
        errorInfo={{
          error: Boolean(state.email.error),
          errorMessage: state.email.error && (
            <Box data-testid="lost-2fa-email-error">
              <EmailErrorMessage error={state.email.error} />
            </Box>
          ),
        }}
      />
      <AuthInputField
        id="lost-2fa-password"
        testID="lost-2fa-password"
        data-testid="lost-2fa-password"
        value={state.password.value}
        label={textPassword}
        type="password"
        showEye
        onChange={e => onPasswordChange(e.target.value)}
        hideValidationCorrectIcon
        errorInfo={{
          error: Boolean(state.password.error),
          errorMessage: state.password.error && (
            <Box data-testid="lost-2fa-password-error">
              <PasswordErrorMessage error={state.password.error} />
            </Box>
          ),
        }}
      />
      <DeltaButton
        aria-roledescription="submit"
        text={textVerify}
        loading={buttonLoading}
        fullWidth
        type="submit"
        data-testid="verify-email-password"
      />
    </form>)
  );
};

export default VerifyEmailPass;
